import { z } from 'zod';
import { validateMutualExclusion } from './stripe-utils';

// Base schema for common fields
export const baseStripeSchema = z.object({
	operation: z.enum([
		'create_payment_intent',
		'confirm_payment_intent',
		'refund_payment',
		'create_customer',
		'attach_payment_method',
		'create_subscription',
		'cancel_subscription',
		'create_invoice',
		'finalize_invoice',
		'send_invoice',
	]),
	stripeAccountMode: z.enum(['platform', 'connect']).optional(),
	connectedAccountIdPath: z.string().optional(),
	currency: z.string().default('usd'),
	waitForWebhook: z.boolean().optional(),
	timeoutSec: z.number().int().min(1).max(3600).default(900),
	idempotencyKeyTemplate: z.string().optional(),
	metadata: z.record(z.string(), z.string()).optional(),
});

// Payment Intent specific schema
export const paymentIntentSchema = z.object({
	operation: z.literal('create_payment_intent'),
	stripeAccountMode: z.enum(['platform', 'connect']).optional(),
	connectedAccountIdPath: z.string().optional(),
	currency: z.string().default('usd'),
	waitForWebhook: z.boolean().optional(),
	timeoutSec: z.number().int().min(1).max(3600).default(900),
	idempotencyKeyTemplate: z.string().optional(),
	metadata: z.record(z.string(), z.string()).optional(),
	amountPath: z.string().min(1, 'Amount path is required'),
	customerIdPath: z.string().optional(),
	paymentMethodIdPath: z.string().optional(),
	confirm: z.boolean().optional(),
	captureMethod: z.enum(['automatic', 'manual']).optional(),
	setupFutureUsage: z.enum(['off_session', 'on_session']).optional(),
}).refine(
	(data) => {
		if (data.stripeAccountMode === 'connect' && !data.connectedAccountIdPath) {
			return false;
		}
		return true;
	},
	{
		message: 'Connected account ID path is required when using connect mode',
		path: ['connectedAccountIdPath'],
	}
);

// Confirm Payment Intent schema
export const confirmPaymentIntentSchema = z.object({
	operation: z.literal('confirm_payment_intent'),
	stripeAccountMode: z.enum(['platform', 'connect']).optional(),
	connectedAccountIdPath: z.string().optional(),
	currency: z.string().default('usd'),
	waitForWebhook: z.boolean().optional(),
	timeoutSec: z.number().int().min(1).max(3600).default(900),
	idempotencyKeyTemplate: z.string().optional(),
	metadata: z.record(z.string(), z.string()).optional(),
	paymentIntentIdPath: z.string().min(1, 'Payment intent ID path is required'),
	paymentMethodIdPath: z.string().optional(),
	captureMethod: z.enum(['automatic', 'manual']).optional(),
}).refine(
	(data) => {
		if (data.stripeAccountMode === 'connect' && !data.connectedAccountIdPath) {
			return false;
		}
		return true;
	},
	{
		message: 'Connected account ID path is required when using connect mode',
		path: ['connectedAccountIdPath'],
	}
);

// Refund schema
export const refundSchema = z.object({
	operation: z.literal('refund_payment'),
	stripeAccountMode: z.enum(['platform', 'connect']).optional(),
	connectedAccountIdPath: z.string().optional(),
	currency: z.string().default('usd'),
	waitForWebhook: z.boolean().optional(),
	timeoutSec: z.number().int().min(1).max(3600).default(900),
	idempotencyKeyTemplate: z.string().optional(),
	metadata: z.record(z.string(), z.string()).optional(),
	paymentIntentIdPath: z.string().optional(),
	chargeIdPath: z.string().optional(),
	refundAmountPath: z.string().optional(),
}).refine(
	(data) => {
		// Either paymentIntentIdPath or chargeIdPath must be provided, but not both
		const mutualExclusion = validateMutualExclusion(
			data.paymentIntentIdPath,
			data.chargeIdPath,
			'Payment Intent ID',
			'Charge ID'
		);
		return mutualExclusion.valid;
	},
	{
		message: 'Provide either Payment Intent ID or Charge ID (not both)',
		path: ['paymentIntentIdPath'],
	}
).refine(
	(data) => {
		if (data.stripeAccountMode === 'connect' && !data.connectedAccountIdPath) {
			return false;
		}
		return true;
	},
	{
		message: 'Connected account ID path is required when using connect mode',
		path: ['connectedAccountIdPath'],
	}
);

// Customer creation schema
export const customerSchema = z.object({
	operation: z.literal('create_customer'),
	stripeAccountMode: z.enum(['platform', 'connect']).optional(),
	connectedAccountIdPath: z.string().optional(),
	currency: z.string().default('usd'),
	waitForWebhook: z.boolean().optional(),
	timeoutSec: z.number().int().min(1).max(3600).default(900),
	idempotencyKeyTemplate: z.string().optional(),
	metadata: z.record(z.string(), z.string()).optional(),
	customerEmailPath: z.string().min(1, 'Customer email path is required'),
	customerNamePath: z.string().optional(),
}).refine(
	(data) => {
		if (data.stripeAccountMode === 'connect' && !data.connectedAccountIdPath) {
			return false;
		}
		return true;
	},
	{
		message: 'Connected account ID path is required when using connect mode',
		path: ['connectedAccountIdPath'],
	}
);

// Attach Payment Method schema
export const attachPaymentMethodSchema = z.object({
	operation: z.literal('attach_payment_method'),
	stripeAccountMode: z.enum(['platform', 'connect']).optional(),
	connectedAccountIdPath: z.string().optional(),
	currency: z.string().default('usd'),
	waitForWebhook: z.boolean().optional(),
	timeoutSec: z.number().int().min(1).max(3600).default(900),
	idempotencyKeyTemplate: z.string().optional(),
	metadata: z.record(z.string(), z.string()).optional(),
	attachCustomerIdPath: z.string().min(1, 'Customer ID path is required'),
	attachPaymentMethodIdPath: z.string().min(1, 'Payment method ID path is required'),
	setDefault: z.boolean().optional(),
}).refine(
	(data) => {
		if (data.stripeAccountMode === 'connect' && !data.connectedAccountIdPath) {
			return false;
		}
		return true;
	},
	{
		message: 'Connected account ID path is required when using connect mode',
		path: ['connectedAccountIdPath'],
	}
);

// Subscription schema
export const subscriptionSchema = z.object({
	operation: z.literal('create_subscription'),
	stripeAccountMode: z.enum(['platform', 'connect']).optional(),
	connectedAccountIdPath: z.string().optional(),
	currency: z.string().default('usd'),
	waitForWebhook: z.boolean().optional(),
	timeoutSec: z.number().int().min(1).max(3600).default(900),
	idempotencyKeyTemplate: z.string().optional(),
	metadata: z.record(z.string(), z.string()).optional(),
	subCustomerIdPath: z.string().min(1, 'Customer ID path is required'),
	priceIdPath: z.string().min(1, 'Price ID path is required'),
	trialDaysPath: z.string().optional(),
	cancelAtPeriodEnd: z.boolean().optional(),
}).refine(
	(data) => {
		if (data.stripeAccountMode === 'connect' && !data.connectedAccountIdPath) {
			return false;
		}
		return true;
	},
	{
		message: 'Connected account ID path is required when using connect mode',
		path: ['connectedAccountIdPath'],
	}
);

// Cancel Subscription schema
export const cancelSubscriptionSchema = z.object({
	operation: z.literal('cancel_subscription'),
	stripeAccountMode: z.enum(['platform', 'connect']).optional(),
	connectedAccountIdPath: z.string().optional(),
	currency: z.string().default('usd'),
	waitForWebhook: z.boolean().optional(),
	timeoutSec: z.number().int().min(1).max(3600).default(900),
	idempotencyKeyTemplate: z.string().optional(),
	metadata: z.record(z.string(), z.string()).optional(),
	subscriptionIdPath: z.string().min(1, 'Subscription ID path is required'),
	cancelAtPeriodEnd: z.boolean().optional(),
}).refine(
	(data) => {
		if (data.stripeAccountMode === 'connect' && !data.connectedAccountIdPath) {
			return false;
		}
		return true;
	},
	{
		message: 'Connected account ID path is required when using connect mode',
		path: ['connectedAccountIdPath'],
	}
);

// Invoice creation schema
export const createInvoiceSchema = z.object({
	operation: z.literal('create_invoice'),
	stripeAccountMode: z.enum(['platform', 'connect']).optional(),
	connectedAccountIdPath: z.string().optional(),
	currency: z.string().default('usd'),
	waitForWebhook: z.boolean().optional(),
	timeoutSec: z.number().int().min(1).max(3600).default(900),
	idempotencyKeyTemplate: z.string().optional(),
	metadata: z.record(z.string(), z.string()).optional(),
	invoiceCustomerIdPath: z.string().min(1, 'Customer ID path is required'),
	invoiceAutoAdvance: z.boolean().optional(),
	sendToEmailPath: z.string().optional(),
}).refine(
	(data) => {
		if (data.stripeAccountMode === 'connect' && !data.connectedAccountIdPath) {
			return false;
		}
		return true;
	},
	{
		message: 'Connected account ID path is required when using connect mode',
		path: ['connectedAccountIdPath'],
	}
);

// Finalize Invoice schema
export const finalizeInvoiceSchema = z.object({
	operation: z.literal('finalize_invoice'),
	stripeAccountMode: z.enum(['platform', 'connect']).optional(),
	connectedAccountIdPath: z.string().optional(),
	currency: z.string().default('usd'),
	waitForWebhook: z.boolean().optional(),
	timeoutSec: z.number().int().min(1).max(3600).default(900),
	idempotencyKeyTemplate: z.string().optional(),
	metadata: z.record(z.string(), z.string()).optional(),
	invoiceIdPath: z.string().min(1, 'Invoice ID path is required'),
}).refine(
	(data) => {
		if (data.stripeAccountMode === 'connect' && !data.connectedAccountIdPath) {
			return false;
		}
		return true;
	},
	{
		message: 'Connected account ID path is required when using connect mode',
		path: ['connectedAccountIdPath'],
	}
);

// Send Invoice schema
export const sendInvoiceSchema = z.object({
	operation: z.literal('send_invoice'),
	stripeAccountMode: z.enum(['platform', 'connect']).optional(),
	connectedAccountIdPath: z.string().optional(),
	currency: z.string().default('usd'),
	waitForWebhook: z.boolean().optional(),
	timeoutSec: z.number().int().min(1).max(3600).default(900),
	idempotencyKeyTemplate: z.string().optional(),
	metadata: z.record(z.string(), z.string()).optional(),
	invoiceIdPath: z.string().min(1, 'Invoice ID path is required'),
	sendToEmailPath: z.string().optional(),
}).refine(
	(data) => {
		if (data.stripeAccountMode === 'connect' && !data.connectedAccountIdPath) {
			return false;
		}
		return true;
	},
	{
		message: 'Connected account ID path is required when using connect mode',
		path: ['connectedAccountIdPath'],
	}
);

// Main schema that selects the appropriate validation based on operation
export const stripeNodeSchema = z.discriminatedUnion('operation', [
	paymentIntentSchema,
	confirmPaymentIntentSchema,
	refundSchema,
	customerSchema,
	attachPaymentMethodSchema,
	subscriptionSchema,
	cancelSubscriptionSchema,
	createInvoiceSchema,
	finalizeInvoiceSchema,
	sendInvoiceSchema,
]);

// Helper function to get the appropriate schema for an operation
export function getSchemaForOperation(operation: string) {
	switch (operation) {
		case 'create_payment_intent':
			return paymentIntentSchema;
		case 'confirm_payment_intent':
			return confirmPaymentIntentSchema;
		case 'refund_payment':
			return refundSchema;
		case 'create_customer':
			return customerSchema;
		case 'attach_payment_method':
			return attachPaymentMethodSchema;
		case 'create_subscription':
			return subscriptionSchema;
		case 'cancel_subscription':
			return cancelSubscriptionSchema;
		case 'create_invoice':
			return createInvoiceSchema;
		case 'finalize_invoice':
			return finalizeInvoiceSchema;
		case 'send_invoice':
			return sendInvoiceSchema;
		default:
			return baseStripeSchema;
	}
}
